import React, { Component } from "react";
import clsx from "clsx";

import BoModal from "../common/BoModal";
import BackofficeService from "../../../services/BackofficeService";
import { I } from "../common/icons";
import { CADENCES, FIRST_SENDS, FREQUENCIES } from "../backofficeConstants";
import { addDays, fmtLong } from "../backofficeUtils";

const CADENCE_DAYS = { every_2_days: 2, every_3_days: 3, weekly: 7, once: 0 };

/**
 * The client email automation attached to a task: recipient, copy, cadence, schedule preview,
 * and the Save & Activate / Send now / Stop actions.
 */
class EmailAutomationModal extends Component {
	constructor(props) {
		super(props);
		const { task, client } = props;
		const e = task.emailAutomation;
		const contact = client && client.contact;
		this.state = {
			saving: false,
			error: null,
			recipientEmail: e ? e.recipientEmail : contact ? contact.email || "" : "",
			recipientName: e ? e.recipientName || "" : contact ? contact.name : "",
			ccEmails: e ? e.ccEmails || "" : "",
			subject: e ? e.subject : `${task.title} request`,
			body: e ? e.body || "" : "",
			cadence: e ? e.cadence : "every_3_days",
			firstSend: e ? e.firstSend : "on_start",
			sendTime: e ? e.sendTime : "09:00",
			markOnCalendar: e ? e.markOnCalendar : true,
			autoStop: e ? e.autoStop : true,
		};
	}

	set = (k, v) => this.setState({ [k]: v });

	payload() {
		const s = this.state;
		return {
			enabled: true,
			recipientEmail: s.recipientEmail,
			recipientName: s.recipientName,
			ccEmails: s.ccEmails,
			subject: s.subject,
			body: s.body,
			cadence: s.cadence,
			firstSend: s.firstSend,
			sendTime: s.sendTime,
			markOnCalendar: s.markOnCalendar,
			autoStop: s.autoStop,
		};
	}

	previewSends() {
		const { task } = this.props;
		const e = task.emailAutomation;
		if (e && e.status === "active" && e.sends.length) return e.sends.filter(s => s.status !== "skipped").map(s => ({ date: s.scheduledFor, time: (s.scheduledTime || "09:00").slice(0, 5), status: s.status, sequence: s.sequence }));
		const first = this.state.firstSend === "day_before_start" ? addDays(task.startDate, -1) : this.state.firstSend === "on_due" ? task.dueDate : task.startDate;
		const step = CADENCE_DAYS[this.state.cadence];
		const out = [{ date: first, time: this.state.sendTime, status: "preview", sequence: 0 }];
		if (step > 0) {
			const horizon = addDays(task.dueDate, 14);
			let next = addDays(first, step);
			while (next <= horizon && out.length < 6) {
				out.push({ date: next, time: this.state.sendTime, status: "preview", sequence: out.length });
				next = addDays(next, step);
			}
		}
		return out;
	}

	save = async () => {
		if (!this.state.recipientEmail) return this.setState({ error: "The client has no contact email. Add a contact to the client first." });
		this.setState({ saving: true, error: null });
		const result = await BackofficeService.upsertTaskEmail(Object.assign({ taskId: this.props.task.id }, this.payload()));
		this.setState({ saving: false });
		if (!result.ok) return this.setState({ error: result.error });
		this.props.onSaved(result.data, "Email automation activated. Sends are marked on the calendar.");
	};

	sendNow = async () => {
		if (!this.state.recipientEmail) return this.setState({ error: "The client has no contact email. Add a contact to the client first." });
		this.setState({ saving: true, error: null });
		let result = await BackofficeService.upsertTaskEmail(Object.assign({ taskId: this.props.task.id }, this.payload()));
		if (result.ok) result = await BackofficeService.sendTaskEmailNow({ taskId: this.props.task.id });
		this.setState({ saving: false });
		if (!result.ok) return this.setState({ error: result.error });
		const from = this.props.gmailStatus && this.props.gmailStatus.connected ? this.props.gmailStatus.gmailAddress : "Workflow mail";
		this.props.onSaved(result.data, `Email sent from ${from}. Follow-up cadence starts from today.`);
	};

	stop = async () => {
		this.setState({ saving: true, error: null });
		const result = await BackofficeService.stopTaskEmail({ taskId: this.props.task.id });
		this.setState({ saving: false });
		if (!result.ok) return this.setState({ error: result.error });
		this.props.onSaved(result.data, "Email automation stopped. No further sends are scheduled.");
	};

	remove = async () => {
		this.setState({ saving: true, error: null });
		const result = await BackofficeService.removeTaskEmail({ taskId: this.props.task.id });
		this.setState({ saving: false });
		if (!result.ok) return this.setState({ error: result.error });
		this.props.onSaved(result.data, "Email automation removed from this task.");
	};

	render() {
		const { task, gmailStatus, onClose, onSwitchAccount } = this.props;
		const s = this.state;
		const e = task.emailAutomation;
		const connected = gmailStatus && gmailStatus.connected;
		const sends = this.previewSends();
		const sentCount = e ? e.sends.filter(x => x.status === "sent").length : 0;

		return (
			<BoModal
				title="Automated Client Email Automation"
				icon={<I.mail />}
				onClose={onClose}
				footer={
					<>
						{e && (
							<button className="bo-btn bo-btn--danger" type="button" onClick={e.status === "active" ? this.stop : this.remove} disabled={s.saving}>
								{e.status === "active" ? "Stop automation" : "Remove"}
							</button>
						)}
						<button className="bo-btn" type="button" onClick={onClose}>
							Cancel
						</button>
						<button className="bo-btn" type="button" onClick={this.sendNow} disabled={s.saving}>
							<I.send />
							Send now
						</button>
						<button className="bo-btn bo-btn--primary ft-bo-email-save" type="button" onClick={this.save} disabled={s.saving}>
							<I.send />
							{s.saving ? "Saving…" : e && e.status === "active" ? "Save changes" : "Save & Activate Email Automation"}
						</button>
					</>
				}
			>
				{s.error && <div className="bo-field__error">{s.error}</div>}
				<div className={clsx("bo-acct", { "bo-acct--off": !connected })}>
					<div className="bo-avatar bo-avatar--g">G</div>
					<div className="bo-acct__who">
						<b>{connected ? `Sending as ${gmailStatus.gmailAddress}` : "No Gmail account connected"}</b>
						<span>{connected ? "Google Workspace · sent items land in your Gmail Sent folder; replies come to your inbox" : "Sends go out through Workflow's mail service with your email as reply-to. Connect Gmail to send as yourself."}</span>
					</div>
					<button className="bo-btn bo-btn--sm" type="button" onClick={onSwitchAccount}>
						{connected ? "Switch account" : "Connect Gmail"}
					</button>
				</div>
				{e && e.status !== "active" && (
					<div className="bo-callout">
						<h4>
							<I.alert />
							This automation is {e.status}
							{sentCount ? ` · ${sentCount} email${sentCount > 1 ? "s" : ""} sent` : ""}
						</h4>
						<div className="bo-callout__note">Saving re-activates it and schedules sends from today.</div>
					</div>
				)}
				<div className="bo-field">
					<label>Recipient Email</label>
					<input type="text" readOnly value={s.recipientEmail ? `${s.recipientEmail}${s.recipientName ? ` (${s.recipientName})` : ""}` : "No client contact email"} />
				</div>
				<div className="bo-field">
					<label>
						CC <small>(optional, comma separated)</small>
					</label>
					<input type="text" value={s.ccEmails} onChange={ev => this.set("ccEmails", ev.target.value)} placeholder="bookkeeping@client.com" />
				</div>
				<div className="bo-field">
					<label>Email Subject Line</label>
					<input type="text" value={s.subject} onChange={ev => this.set("subject", ev.target.value)} />
				</div>
				<div className="bo-field">
					<label>
						Message Body Template <small>{"{{contact_first_name}} {{client_name}} {{period}} {{task_name}} {{due_date}}"}</small>
					</label>
					<textarea value={s.body} onChange={ev => this.set("body", ev.target.value)} placeholder="Please upload your September bank and credit card statements via the portal link." />
				</div>
				<div className="bo-callout">
					<h4>
						<I.bell />
						Automated Follow-Up Cadence
					</h4>
					<div className="bo-field">
						<select value={s.cadence} onChange={ev => this.set("cadence", ev.target.value)}>
							{Object.keys(CADENCES).map(k => (
								<option key={k} value={k}>
									{CADENCES[k]}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="bo-callout bo-callout--blue">
					<h4>
						<I.refresh />
						Recurring schedule
					</h4>
					<div className="bo-grid3">
						<div className="bo-field">
							<label>Repeat with task</label>
							<input type="text" readOnly value={`${FREQUENCIES[task.frequency]} (follows task)`} />
						</div>
						<div className="bo-field">
							<label>First send</label>
							<select value={s.firstSend} onChange={ev => this.set("firstSend", ev.target.value)}>
								{Object.keys(FIRST_SENDS).map(k => (
									<option key={k} value={k}>
										{FIRST_SENDS[k]}
									</option>
								))}
							</select>
						</div>
						<div className="bo-field">
							<label>Send time</label>
							<input type="time" value={s.sendTime} onChange={ev => this.set("sendTime", ev.target.value)} />
						</div>
					</div>
					<div className="bo-toggle-row">
						<div>
							<div className="bo-toggle-row__t">Mark sends on the calendar</div>
							<div className="bo-toggle-row__d">Each scheduled send and follow-up appears as a dashed email event. Skip one from its popover to reschedule.</div>
						</div>
						<button className={clsx("bo-tog", { "bo-tog--on": s.markOnCalendar })} type="button" aria-label="Toggle calendar marks" onClick={() => this.set("markOnCalendar", !s.markOnCalendar)} />
					</div>
					<div>
						<div className="bo-callout__sub" style={{ marginBottom: 6 }}>
							{e && e.status === "active" ? "Scheduled sends" : "Next scheduled sends (preview)"}
						</div>
						<div className="bo-sendlist">
							{sends.slice(0, 4).map((x, i) => (
								<span key={i} className={clsx("bo-datechip", { "bo-datechip--sent": x.status === "sent" })}>
									<I.mail />
									{fmtLong(x.date)} · {x.time}
									{x.sequence === 0 ? " · first" : ""}
									{x.status === "sent" ? " · sent" : ""}
								</span>
							))}
							{sends.length > 4 && <span className="bo-datechip">+{sends.length - 4} more</span>}
						</div>
					</div>
					<div className="bo-toggle-row">
						<div>
							<div className="bo-toggle-row__t">Stop automatically</div>
							<div className="bo-toggle-row__d">Stops when the task is marked complete.</div>
						</div>
						<button className={clsx("bo-tog", { "bo-tog--on": s.autoStop })} type="button" aria-label="Toggle auto stop" onClick={() => this.set("autoStop", !s.autoStop)} />
					</div>
				</div>
			</BoModal>
		);
	}
}

export default EmailAutomationModal;
